import React from 'react'
import { Container, Header, Divider } from 'semantic-ui-react'

import SEO from 'lib/components/SEO'
import ExternalLink from 'lib/components/ExternalLink'

import SpeedGraph from './SpeedGraph'
import CTA from './CTA'
import Section from './Section'

const Drugs = ({}) => {
  return (
    <>
      <SEO
        title="Medicine and drug recalls in Canada | Recall Monitor"
        description="Recalled drugs can have life-threatening effects if not addressed quickly. 2019 had 80+ recalled medication in Canada. Learn about major recalls and side-effects on Recall Monitor."
        canonical="https://recallmonitor.ca/why/drugs"
        type="article"
        image="https://recallmonitor.ca/static/why/other-drugs.jpeg"
      />
      <Container text css={{ margin: '1rem 0' }}>
        <Header as="h1">
          Medicine and Drugs
          <Header.Subheader>
            Avoid recalled drugs and medicine, protect your health
          </Header.Subheader>
        </Header>
        <p>
          Every year hundreds of drugs are recalled for various reasons. Since
          2019 there were <strong>180+</strong> products that can put your
          health at risk. That is a product recall every <strong>3 days</strong>
          .
        </p>

        <p>
          How can patients and those that use medicines keep themselves
          protected? How can hospitals, clinics and health service providers
          ensure best treatments for their patients?{' '}
          <strong>
            Just a few hours of delay might mean the consumption of recalled
            drugs.
          </strong>
        </p>

        <SpeedGraph />

        <p>
          Recall Monitor can help you keep track of active recalls for drugs,
          keeping you and your patients safe.
        </p>

        <p>
          Hospitals, clinics and health centers can serve their patients better,
          and provide effective treatment. Oversights in missing recalled drugs
          can cause huge issues, like the case of a{' '}
          <ExternalLink href="https://www.ctvnews.ca/health/proposed-class-action-lawsuit-filed-after-blood-pressure-medication-recalled-1.4023577">
            blood pressure medication that was recalled, and patients were left
            in the dark
          </ExternalLink>{' '}
          or the{' '}
          <ExternalLink href="https://www.cbc.ca/news/canada/nova-scotia/invokana-diabetes-drug-faces-canadian-class-action-lawsuit-1.3231463">
            diabetes drug that was the subject of a class-action
          </ExternalLink>
          .
        </p>

        <p>
          Below are some examples of common drug recalls we've seen since 2019
          in Canada.
        </p>

        <Divider />

        <Section img="/static/why/unauthorized-pills.jpg">
          <Header>Unauthorized Pharmaceuticals</Header>
          <p>
            <strong>20</strong> recalls were issued for 50+ unathorized drugs
            because they caused a threat to the health of its users.
          </p>
          <p>
            Even prescription drugs can be unauthorized, generally coming to
            Canada via foreign producers. Male performance pills, slimming teas,
            eye solutions and acne creams are among the medicinens sold often in
            unauthorized retailers.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/sartan.jpg" reversed>
          <Header>Sartan (Blood Pressure Medication)</Header>
          <p>
            <strong>10</strong> recalls for sartan medication, including
            Losartan, irbesartan and fibristal.
          </p>
          <p>
            As these drugs are hard on kidney and liver function, many are
            recalled for containing greater than allowable limits of harmful
            effects. These recalls have resulted in multiple class actions
            stemming from harmful effects to its unaware users.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/cream.jpg">
          <Header>Creams, Gels and Sprays</Header>
          <p>
            <strong>7</strong> recalls involving creams, gels and various
            sprays.
          </p>
          <p>
            Examples include sunscreens, acne medication, topical pain lotions
            and other products.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/painkiller.jpg" reversed>
          <Header>Painkillers</Header>
          <p>
            <strong>6</strong> instances of recalled painkillers.
          </p>
          <p>
            Examples include Ibuprofen, Lyracaine, Hydromorph Contin and Teva.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/other-drugs.jpeg">
          <Header>Other Drugs</Header>
          <p>
            There were <strong>148</strong> other medications that were recalled
            by the CFIA for various reasons.
          </p>
          <p>
            Among these are arthiritis, dialysis, sleep pills, mouthwashes,
            ADHD, cannnabis and cancer medications. Drugs and medicines are the
            widest ranging and the most urgent impact recalls in Canada.
          </p>
        </Section>

        <Divider />

        <Header>The list goes on...</Header>
        <p>
          Were you aware of any drugs recalled since 2019, and the great
          problems they caused?
        </p>
        <p>
          Recall Monitor will help you get knowledgeable about recalled
          products. Don't be at the mercy of a news segment you may never see.
          Sign up now, and start receiving the alerts you care about -
          instantly.
        </p>
        <CTA />
      </Container>
    </>
  )
}

export default Drugs
